import { DataSourceType } from "@coeff/api";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import {
  BulletCheckList,
  CoePlanIcon,
  FlexColumn,
  Typography,
  FlexRow,
  ImportTypeIcon,
  Button,
} from "../../../components";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import { useTrack } from "../../../utils";
import { dataSourceTypeToCommaSeparatedString } from "../billing.utils";

type Props = {
  premiumConnectorsUsed: DataSourceType[];
};

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 426px;
  width: 100%;
  height: 100%;
`;

const LogoContainer = styled.div`
  background: linear-gradient(
    103deg,
    #428bfa 0%,
    #a769e6 35.27%,
    #d850b8 60.74%,
    #ff3353 78.38%,
    #ff8121 96.5%
  );
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  color: ${COLORS.white};
`;

const CollapseToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  margin-top: 8px;
`;

const Arrow = styled.span<{ expanded: boolean }>`
  display: inline-block;
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: ${({ expanded }) => (expanded ? "rotate(90deg)" : "rotate(0deg)")};
`;

const AdminEmailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 140px;
  overflow-y: auto;
  padding: 12px;
  background: ${COLORS.coeblue10};
  border-radius: 6px;
  margin-top: 8px;
`;

const AdminEmail = styled(Typography)`
  color: ${COLORS.coeblue4};
  word-break: break-all;
`;

export const ContactAdminFullScreen: React.FC<Props> = ({ premiumConnectorsUsed }) => {
  const track = useTrack();
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);
  const adminEmails = clientFlags?.admin_emails ?? [];
  const hasPremiumConnectors = premiumConnectorsUsed.length > 0;

  const [emailsExpanded, setEmailsExpanded] = useState(false);

  useEffect(() => {
    track("contact_admin_modal_shown");
  }, []);

  const handleCopyEmails = () => {
    if (adminEmails.length === 0) {
      return;
    }
    track("contact_admin_copy_emails_clicked");
    const emailText = adminEmails.join(", ");
    const tempTextArea = document.createElement("textarea");
    tempTextArea.value = emailText;
    document.body.appendChild(tempTextArea);
    tempTextArea.select();
    document.execCommand("copy");
    document.body.removeChild(tempTextArea);
  };

  return (
    <Layout>
      <FlexColumn gap={2} padding={"40px"}>
        <Typography variant={"h4"} fontWeight={"bold"}>
          Contact your Admin to Upgrade
        </Typography>
        {hasPremiumConnectors ? (
          <Typography variant={"body1"}>
            Your organization has a plan that supports premium data sources (
            {dataSourceTypeToCommaSeparatedString(premiumConnectorsUsed, 2)}). Contact your admin to
            get access.
          </Typography>
        ) : (
          <Typography variant={"body1"}>
            Your organization already has a Coefficient plan. Contact your admin to be added.
          </Typography>
        )}

        <BulletCheckList
          items={[
            <Typography variant={"body2"}>
              {hasPremiumConnectors
                ? `Connect to ${dataSourceTypeToCommaSeparatedString(
                    premiumConnectorsUsed,
                    2
                  )} and all premium data sources`
                : "Connect to all premium data sources"}
            </Typography>,
            <Typography variant={"body2"}>Unlimited run usage + much more</Typography>,
          ]}
        ></BulletCheckList>

        {hasPremiumConnectors && (
          <FlexRow
            style={{
              backgroundColor: COLORS.yellowBg4,
              borderColor: COLORS.yellowBg4,
              padding: "12px",
              maxWidth: "534px",
            }}
            gap={1.5}
          >
            <FlexColumn style={{ maxWidth: "408px" }}>
              <Typography variant={"body2"} fontWeight={"bold"}>
                Premium data sources in use
              </Typography>
              <Typography variant={"body2"} fontWeight={"normal"}>
                Your team is using premium data sources (
                {dataSourceTypeToCommaSeparatedString(premiumConnectorsUsed, 2)}). Contact your
                admin to get access.
              </Typography>
            </FlexColumn>
            <FlexRow style={{ flex: 1 }} reverse>
              {premiumConnectorsUsed.slice(0, 2).map(ds => (
                <ImportTypeIcon type={ds} key={ds} size={40} />
              ))}
            </FlexRow>
          </FlexRow>
        )}

        {adminEmails.length > 0 && (
          <>
            <CollapseToggle onClick={() => setEmailsExpanded(prev => !prev)}>
              <Arrow expanded={emailsExpanded}>&#9654;</Arrow>
              <Typography variant={"body2"} style={{ color: COLORS.black65 }}>
                {adminEmails.length === 1
                  ? "Show admin email"
                  : `Show admin emails (${adminEmails.length})`}
              </Typography>
            </CollapseToggle>

            {emailsExpanded && (
              <AdminEmailList>
                {adminEmails.map((email, idx) => (
                  <AdminEmail key={idx} variant={"body2"}>
                    {email}
                  </AdminEmail>
                ))}
              </AdminEmailList>
            )}
          </>
        )}

        <Button
          type={"primary"}
          style={{ maxWidth: "180px", marginTop: "20px" }}
          onClick={handleCopyEmails}
          disabled={adminEmails.length === 0}
        >
          {adminEmails.length === 1 ? "Copy Admin Email" : "Copy Admin Emails"}
        </Button>
      </FlexColumn>
      <LogoContainer>
        <CoePlanIcon height={88} width={76} fill={COLORS.white} />
      </LogoContainer>
    </Layout>
  );
};
